import { useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

import { api } from '_core/api/api-nextjs-proxy';
import { usePublicAppContext } from '_core/context/public-app-context';
import { AgenciesProps } from '_pages/api/proxy/agencies/find-agencies';

type AgencyClaimStatus = 'CLAIMED' | 'UNCLAIMED' | 'NEW_RECORD';

export type AgenciesAutocompletePayload = AgenciesProps & {
  status: AgencyClaimStatus;
};

type VariableProps = {
  name: string;
};

const cacheKey = {
  findAgencies: '/api/proxy/agencies/find-agencies',
};

const urls = {
  findAgencies: '/api/proxy/agencies/find-agencies',
};

const newRecord = (fragment: string) => ({
  slug: '',
  managed: false,
  website: '',
  name: fragment,
  logo: '',
  status: 'NEW_RECORD' as AgencyClaimStatus,
});

const composePayload = (agencies: AgenciesProps[], fragment: string): AgenciesAutocompletePayload[] => {
  const existingrecords = agencies.map((agency) => {
    const status: AgencyClaimStatus = agency.managed ? 'CLAIMED' : 'UNCLAIMED';
    return { ...agency, status };
  });

  return [newRecord(fragment), ...existingrecords];
};

const agenciesRepo = {
  findAgencies: async (
    variables: VariableProps,
    config?: AxiosRequestConfig,
  ): Promise<AgenciesAutocompletePayload[]> => {
    const response = await api.post(urls.findAgencies, variables, config);
    const agencies = response?.data?.data?.agencies?.nodes;
    return composePayload(agencies, variables.name);
  },
};

export const useAgenciesAutocomplete = (variables: VariableProps, enabled = true) => {
  const { domainInfo } = usePublicAppContext();
  const baseURL = domainInfo?.getOriginUrl() ?? '';
  const { name } = variables;

  return useQuery({
    queryKey: [cacheKey.findAgencies, name],
    queryFn: () => agenciesRepo.findAgencies(variables, { baseURL }),
    enabled,
  });
};
